import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Api from '../../../libraries/api';
import CommonHelper from '../../../libraries/common-helper';
import { TOP_MANAGEMENT_FILTER } from '../../../constants/global';

const TASK_DASHBOARD_API = '/reports/task-dashboard';

const TASK_DASHBOARD_UI = {
    hideZeroTotalCards: true, // Change to false to show all zero cards too
    tabActiveBackground: '#5a7dfd',
    tabInactiveBackground: '#f5f6fa',
    tabActiveBorder: '2px solid #5a7dfd',
    tabInactiveBorder: '1px solid rgba(0,0,0,0.12)',
    tabActiveShadow: '0 4px 16px rgba(90,125,253,0.28)',
    tabInactiveShadow: 'none',
    personCountCircleBackground: '#e8eaf6',
    personCountCircleColor: '#3949ab',
    caseBadgeStyles: {
        HM: { backgroundColor: '#e3f2fd', color: '#1565c0' },
        'P&I': { backgroundColor: '#e0f2f1', color: '#00695c' },
        Shipowner: { backgroundColor: '#f3e5f5', color: '#6a1b9a' },
        Cargo: { backgroundColor: '#fff3e0', color: '#e65100' },
        Others: { backgroundColor: '#eceff1', color: '#546e7a' },
    },
    highCountThreshold: 3,
    highCountRowBackground: '#ffeac8',
    highCountRowHoverBackground: '#ffe0b2',
    highCountAlertIconColor: '#d32f2f',
};

const TASK_DASHBOARD_TABLE_STYLES = `
.task-dashboard-scope .table-list .MuiTableCell-root {
    font-size: 18px !important;
}
.task-dashboard-scope .table-list a.td-table-num-link,
.task-dashboard-scope .table-list a.td-table-name-link {
    font-size: 18px;
}
.task-dashboard-scope .table-list .MuiTableBody-root .MuiTableRow-root.td-table-row--high-count,
.task-dashboard-scope .table-list .MuiTableBody-root .MuiTableRow-root.td-table-row--high-count:nth-child(even) {
    background-color: ${TASK_DASHBOARD_UI.highCountRowBackground} !important;
}
.task-dashboard-scope .table-list .MuiTableBody-root .MuiTableRow-root.td-table-row--high-count td,
.task-dashboard-scope .table-list .MuiTableBody-root .MuiTableRow-root.td-table-row--high-count:nth-child(even) td {
    background-color: ${TASK_DASHBOARD_UI.highCountRowBackground} !important;
}
.task-dashboard-scope .table-list .MuiTableBody-root .MuiTableRow-root.td-table-row--high-count:hover,
.task-dashboard-scope .table-list .MuiTableBody-root .MuiTableRow-root.td-table-row--high-count:nth-child(even):hover {
    background-color: ${TASK_DASHBOARD_UI.highCountRowHoverBackground} !important;
}
.task-dashboard-scope .table-list .MuiTableBody-root .MuiTableRow-root.td-table-row--high-count:hover td,
.task-dashboard-scope .table-list .MuiTableBody-root .MuiTableRow-root.td-table-row--high-count:nth-child(even):hover td {
    background-color: ${TASK_DASHBOARD_UI.highCountRowHoverBackground} !important;
}
.task-dashboard-scope .td-assignment-tabs {
    width: 100%;
    margin-bottom: 4px;
}
.task-dashboard-scope .td-assignment-tabs .MuiTab-root {
    text-transform: none;
    font-size: 16px;
    font-weight: 600;
    min-width: 120px;
}
`;

const TAB_SUMMARY_BADGE_PLACEHOLDER = {
    total: 0,
    case_type_summary: { HM: 0, 'P&I': 0, Shipowner: 0, Cargo: 0, Others: 0 },
};

const CASE_TYPE_KEYS = [
    { param: 'HM', label: 'HM', projectsQueryValue: 'H%26M' },
    { param: 'P&I', label: 'P&I', projectsQueryValue: 'P%26I' },
    { param: 'Shipowner', label: 'Shipowner', projectsQueryValue: 'Shipowner' },
    { param: 'Cargo', label: 'Cargo', projectsQueryValue: 'Cargo' },
    // Others stays off the dashboard. It is not a projects case-type filter.
];

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`task-dash-tabpanel-${index}`} {...other}>
            {value === index ? <Box pt={2}>{children}</Box> : null}
        </div>
    );
}

class TaskDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            loadError: false,
            mainTab: 0,
            reportOnProgress: {
                topManagement: [],
                claimHandlers: [],
                surveyors: [],
            },
            approvalOnProgress: {
                topManagement: [],
                approvalHandlers: [],
                surveyors: [],
            },
            reportSummary: null,
            approvalSummary: null,
            sectionTabs: {},
        };
    }

    componentDidMount() {
        document.title = 'Abadi - Task Dashboard';
        this.fetchDashboard();
    }

    fetchDashboard = () => {
        this.setState({ loading: true, loadError: false });
        Api.get(TASK_DASHBOARD_API)
            .then((resp) => {
                const envelope = resp?.data;
                const body =
                    envelope && typeof envelope === 'object' && envelope.data !== undefined
                        ? envelope.data
                        : envelope;
                const normalized = this.normalizePayload(body);

                const mergedReportTm =
                    normalized.reportOnProgress.topManagement.length > 0
                        ? normalized.reportOnProgress.topManagement
                        : this.fallbackTopManagement('report');
                const mergedApprovalTm =
                    normalized.approvalOnProgress.topManagement.length > 0
                        ? normalized.approvalOnProgress.topManagement
                        : this.fallbackTopManagement('approval');

                this.setState({
                    reportOnProgress: {
                        ...normalized.reportOnProgress,
                        topManagement: mergedReportTm,
                    },
                    approvalOnProgress: {
                        ...normalized.approvalOnProgress,
                        topManagement: mergedApprovalTm,
                    },
                    reportSummary: normalized.reportSummary ?? null,
                    approvalSummary: normalized.approvalSummary ?? null,
                    loading: false,
                    loadError: false,
                });
            })
            .catch(() => {
                this.setState({
                    ...this.emptyDataset(),
                    loading: false,
                    loadError: true,
                });
            });
    };

    peopleWithEmptyTasks(people) {
        if (!Array.isArray(people)) return [];
        return people.filter((person) => {
            const total = person?.total;
            if (total === null || total === undefined || total === '') return false;
            const count = Number(total);
            return Number.isFinite(count) && count === 0;
        });
    }

    setSectionTab = (sectionKey, value) => {
        this.setState((prev) => ({
            sectionTabs: {
                ...prev.sectionTabs,
                [sectionKey]: value,
            },
        }));
    };

    projectsUrlWithCaseType(filterBase, caseTypeParam) {
        if (!filterBase || typeof filterBase !== 'object') return '#';
        const key = CASE_TYPE_KEYS.find((k) => k.param === caseTypeParam);
        const caseTypeForProjects = key?.projectsQueryValue ?? caseTypeParam;
        return this.buildProjectsUrl({
            ...filterBase,
            ...(caseTypeParam ? { case_type: caseTypeForProjects } : {}),
        });
    }

    // =============================================================================
    // Helpers: Payload normalization & mapping
    // =============================================================================

    mapReportFinalReviewers(list, reportTypeId) {
        if (!Array.isArray(list)) return [];
        return list.map((row) => {
            const ids = this.reportStatusIdsFromRow(row);
            const { hm, pi, shipowner, cargo, others } = this.pickCaseCountsFromSummary(row.case_type_summary);
            const filterBase = {
                ...(reportTypeId ? { typeId: reportTypeId } : {}),
                ...(ids.length ? { multiProjectStatusReportId: ids.join(',') } : {}),
                ...(row.user_id ? { finalReviewerId: row.user_id } : {}),
            };
            return {
                id: row.user_id,
                name: row.name,
                total: this.pickTotal(row),
                hm,
                pi,
                shipowner,
                cargo,
                others,
                filterBase,
                projectsUrl: this.buildProjectsUrl(filterBase),
            };
        });
    }

    mapApprovalFinalReviewers(list, approvalTypeId) {
        if (!Array.isArray(list)) return [];
        return list.map((row) => {
            const ids = this.reportStatusIdsFromRow(row);
            const { hm, pi, shipowner, cargo, others } = this.pickCaseCountsFromSummary(row.case_type_summary);
            const filterBase = {
                ...(approvalTypeId ? { typeId: approvalTypeId } : {}),
                ...(ids.length ? { multiProjectStatusApprovalId: ids.join(',') } : {}),
                ...(row.user_id ? { finalReviewerId: row.user_id } : {}),
            };
            return {
                id: row.user_id,
                name: row.name,
                total: this.pickTotal(row),
                hm,
                pi,
                shipowner,
                cargo,
                others,
                filterBase,
                projectsUrl: this.buildProjectsUrl(filterBase),
            };
        });
    }

    mapReportClaimHandlers(list, reportTypeId) {
        if (!Array.isArray(list)) return [];
        return list.map((row) => {
            const ids = this.reportStatusIdsFromRow(row);
            const { hm, pi, shipowner, cargo, others } = this.pickCaseCountsFromSummary(row.case_type_summary);
            const filterBase = {
                ...(reportTypeId ? { typeId: reportTypeId } : {}),
                ...(ids.length ? { multiProjectStatusReportId: ids.join(',') } : {}),
                ...(row.user_id ? { adminId: row.user_id } : {}),
            };
            return {
                id: row.user_id,
                name: row.name,
                total: this.pickTotal(row),
                hm,
                pi,
                shipowner,
                cargo,
                others,
                filterBase,
                projectsUrl: this.buildProjectsUrl(filterBase),
            };
        });
    }

    mapApprovalClaimHandlers(list, approvalTypeId) {
        if (!Array.isArray(list)) return [];
        return list.map((row) => {
            const ids = this.reportStatusIdsFromRow(row);
            const filterBase = {
                ...(approvalTypeId ? { typeId: approvalTypeId } : {}),
                ...(ids.length ? { multiProjectStatusApprovalId: ids.join(',') } : {}),
                ...(row.user_id ? { adminId: row.user_id } : {}),
            };
            return {
                id: row.user_id,
                name: row.name,
                total: this.pickTotal(row),
                hm: null,
                pi: null,
                shipowner: null,
                cargo: null,
                others: null,
                filterBase,
                projectsUrl: this.buildProjectsUrl(filterBase),
            };
        });
    }

    mapReportSurveyors(list, reportTypeId) {
        if (!Array.isArray(list)) return [];
        return list.map((row) => {
            const ids = this.reportStatusIdsFromRow(row);
            const { hm, pi, shipowner, cargo, others } = this.pickCaseCountsFromSummary(row.case_type_summary);
            const filterBase = {
                ...(reportTypeId ? { typeId: reportTypeId } : {}),
                ...(ids.length ? { multiProjectStatusReportId: ids.join(',') } : {}),
                surveyorId: row.user_id,
            };
            return {
                id: row.user_id,
                name: row.name,
                total: this.pickTotal(row),
                hm,
                pi,
                shipowner,
                cargo,
                others,
                filterBase,
                projectsUrl: this.buildProjectsUrl(filterBase),
            };
        });
    }

    mapApprovalSurveyors(list, approvalTypeId) {
        if (!Array.isArray(list)) return [];
        return list.map((row) => {
            const ids = this.reportStatusIdsFromRow(row);
            const filterBase = {
                ...(approvalTypeId ? { typeId: approvalTypeId } : {}),
                ...(ids.length ? { multiProjectStatusApprovalId: ids.join(',') } : {}),
                surveyorId: row.user_id,
            };
            return {
                id: row.user_id,
                name: row.name,
                total: this.pickTotal(row),
                hm: null,
                pi: null,
                shipowner: null,
                cargo: null,
                others: null,
                filterBase,
                projectsUrl: this.buildProjectsUrl(filterBase),
            };
        });
    }

    normalizePayload(raw) {
        const d = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
        const report = d.reportOnProgress ?? d.report_on_progress ?? {};
        const approval = d.approvalOnProgress ?? d.approval_on_progress ?? {};

        const reportTypeId = report.type && report.type.id ? report.type.id : '';
        const approvalTypeId = approval.type && approval.type.id ? approval.type.id : '';

        const finalRep = report.final_reviewers ?? report.finalReviewers ?? [];
        const claimRep = report.claim_handlers ?? report.claimHandlers ?? [];
        const survRep = report.surveyors ?? [];

        const finalApp = approval.final_reviewers ?? approval.finalReviewers ?? [];
        const claimApp = approval.claim_handlers ?? approval.claimHandlers ?? [];
        const survApp = approval.surveyors ?? [];

        return {
            reportOnProgress: {
                topManagement: this.mapReportFinalReviewers(finalRep, reportTypeId),
                claimHandlers: this.mapReportClaimHandlers(claimRep, reportTypeId),
                surveyors: this.mapReportSurveyors(survRep, reportTypeId),
            },
            approvalOnProgress: {
                topManagement: this.mapApprovalFinalReviewers(finalApp, approvalTypeId),
                approvalHandlers: this.mapApprovalClaimHandlers(claimApp, approvalTypeId),
                surveyors: this.mapApprovalSurveyors(survApp, approvalTypeId),
            },
            reportSummary: report.summary ?? null,
            approvalSummary: approval.summary ?? null,
        };
    }

    fallbackTopManagement(mode) {
        const keys = Object.keys(TOP_MANAGEMENT_FILTER);
        const labels = { alif: 'Pak Alif', fauzi: 'Pak Fauzi', faishal: 'Pak Faishal' };
        return keys.map((key) => {
            const f = TOP_MANAGEMENT_FILTER[key];
            const reportIds = f.report || [];
            const approvalIds = f.approval || [];
            const projectsUrl =
                mode === 'approval' && approvalIds.length && !reportIds.length
                    ? this.buildProjectsUrlFromStatusIds([], approvalIds)
                    : this.buildProjectsUrlFromStatusIds(reportIds, approvalIds);
            const filterBase =
                mode === 'approval' && approvalIds.length && !reportIds.length
                    ? { multiProjectStatusApprovalId: approvalIds.join(',') }
                    : {
                          ...(reportIds.length ? { multiProjectStatusReportId: reportIds.join(',') } : {}),
                          ...(approvalIds.length ? { multiProjectStatusApprovalId: approvalIds.join(',') } : {}),
                      };
            return {
                id: key,
                name: labels[key] || key,
                total: null,
                hm: null,
                pi: null,
                shipowner: null,
                cargo: null,
                others: null,
                filterBase,
                projectsUrl,
            };
        });
    }

    emptyDataset() {
        return {
            reportOnProgress: {
                topManagement: this.fallbackTopManagement('report'),
                claimHandlers: [],
                surveyors: [],
            },
            approvalOnProgress: {
                topManagement: this.fallbackTopManagement('approval'),
                approvalHandlers: [],
                surveyors: [],
            },
            reportSummary: null,
            approvalSummary: null,
        };
    }

    caseCountForParam(person, param) {
        const map = { HM: 'hm', 'P&I': 'pi', Shipowner: 'shipowner', Cargo: 'cargo', Others: 'others' };
        return person[map[param]];
    }

    filterPeopleForDisplay(people) {
        if (!TASK_DASHBOARD_UI.hideZeroTotalCards || !Array.isArray(people)) {
            return people || [];
        }
        return people.filter((p) => {
            const t = p.total;
            if (t === null || t === undefined) return true;
            return Number(t) > 0;
        });
    }
    
    buildProjectsUrl(filters = {}) {
        const parts = [];
        const caseType = filters.case_type ?? filters.caseType;
        const typeId = filters.typeId ?? filters.type_id;
        const reportIds = CommonHelper.splitIdList(filters.multiProjectStatusReportId ?? filters.multi_project_status_report_id);
        const approvalIds = CommonHelper.splitIdList(filters.multiProjectStatusApprovalId ?? filters.multi_project_status_approval_id);
        const surveyorId = filters.surveyorId ?? filters.surveyor_id ?? filters.user_id;
        const adminId = filters.adminId ?? filters.admin_id;
        const finalReviewerId = filters.finalReviewerId ?? filters.final_reviewer_id;

        if (caseType !== undefined && caseType !== null && String(caseType).length) {
            parts.push(`case_type=${String(caseType)}`);
        }
        if (typeId !== undefined && typeId !== null && String(typeId).length) {
            parts.push(`typeId=${encodeURIComponent(String(typeId))}`);
        }
        reportIds.forEach((id) => {
            parts.push(`multiProjectStatusReportId[]=${encodeURIComponent(String(id))}`);
        });
        approvalIds.forEach((id) => {
            parts.push(`multiProjectStatusApprovalId[]=${encodeURIComponent(String(id))}`);
        });
        if (surveyorId !== undefined && surveyorId !== null && String(surveyorId).length) {
            parts.push(`surveyorId=${encodeURIComponent(String(surveyorId))}`);
        }
        if (adminId !== undefined && adminId !== null && String(adminId).length) {
            parts.push(`adminId=${encodeURIComponent(String(adminId))}`);
        }
        if (finalReviewerId !== undefined && finalReviewerId !== null && String(finalReviewerId).length) {
            parts.push(`finalReviewerId=${encodeURIComponent(String(finalReviewerId))}`);
        }

        return parts.length ? `/projects?${parts.join('&')}` : '/projects';
    }

    buildProjectsUrlFromStatusIds(reportIds = [], approvalIds = []) {
        return this.buildProjectsUrl({
            multiProjectStatusReportId: reportIds,
            multiProjectStatusApprovalId: approvalIds,
        });
    }

    pickTotal(row) {
        const t = row.total ?? row.jumlah ?? row.count;
        return t === null || t === undefined ? null : Number(t);
    }

    pickCaseCountsFromSummary(summary) {
        if (!summary || typeof summary !== 'object' || Array.isArray(summary)) {
            return { hm: null, pi: null, shipowner: null, cargo: null, others: null };
        }
        return {
            hm: summary.HM,
            pi: summary['P&I'],
            shipowner: summary.Shipowner,
            cargo: summary.Cargo,
            others: summary.Others,
        };
    }

    reportStatusIdsFromRow(row) {
        if (row.project_statuses && Array.isArray(row.project_statuses)) {
            return row.project_statuses.map((s) => s && s.id).filter(Boolean);
        }
        if (row.project_status && row.project_status.id) {
            return [row.project_status.id];
        }
        return [];
    }

    pickSummaryTotals(summaryBlock) {
        if (!summaryBlock || typeof summaryBlock !== 'object') return null;
        const cts = summaryBlock.case_type_summary;
        const counts = this.pickCaseCountsFromSummary(cts || {});
        return {
            total: summaryBlock.total,
            ...counts,
        };
    }

    handleMainTab = (index) => {
        this.setState({ mainTab: index });
    };

    caseBadgeInnerPillStyle(chipStyle, onPrimaryBlue) {
        if (onPrimaryBlue) {
            return {
                backgroundColor: 'rgba(255,255,255,0.38)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.55)',
            };
        }
        const bgHex = chipStyle.backgroundColor || '#eeeeee';
        if (!CommonHelper.hexToRgb(bgHex)) {
            return {
                backgroundColor: 'rgba(0,0,0,0.12)',
                color: chipStyle.color || '#333',
                border: '1px solid rgba(0,0,0,0.08)',
            };
        }
        return {
            color: chipStyle.color || '#333',
            border: '1px solid rgba(0,0,0,0.07)',
        };
    }

    getMainTabPaperStyle(isSelected) {
        return {
            cursor: 'pointer',
            padding: '22px 24px',
            borderRadius: 12,
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            backgroundColor: isSelected ? TASK_DASHBOARD_UI.tabActiveBackground : TASK_DASHBOARD_UI.tabInactiveBackground,
            border: isSelected ? TASK_DASHBOARD_UI.tabActiveBorder : TASK_DASHBOARD_UI.tabInactiveBorder,
            color: 'rgba(0,0,0,0.87)',
            boxShadow: isSelected ? TASK_DASHBOARD_UI.tabActiveShadow : TASK_DASHBOARD_UI.tabInactiveShadow,
            outline: 'none',
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        };
    }

    renderMainViewTabCard({ tabIndex, title, badgesContent, totalCircleContent }) {
        const { mainTab } = this.state;
        const isSelected = mainTab === tabIndex;
        return (
            <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
                <Paper
                    role="tab"
                    id={`task-dash-tab-${tabIndex}`}
                    aria-selected={isSelected}
                    aria-controls={`task-dash-tabpanel-${tabIndex}`}
                    tabIndex={isSelected ? 0 : -1}
                    onClick={() => this.handleMainTab(tabIndex)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            this.handleMainTab(tabIndex);
                        }
                    }}
                    elevation={isSelected ? 2 : 0}
                    className={`td-tab-card${isSelected ? ' td-tab-card--active' : ''}`}
                    style={this.getMainTabPaperStyle(isSelected)}
                >
                    <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                    >
                        <Box flex={1} minWidth={0} pr={1}>
                            <Typography
                                variant="h6"
                                style={{
                                    fontWeight: 700,
                                    color: isSelected ? '#fff' : '#1a1a1a',
                                }}
                            >
                                {title}
                            </Typography>
                            {badgesContent}
                        </Box>
                        {totalCircleContent}
                    </Box>
                </Paper>
            </Grid>
        );
    }

    renderTabSummaryBadges(summaryBlock, onPrimaryBlue) {
        if (!summaryBlock) return null;
        const row = this.pickSummaryTotals(summaryBlock) || {};
        const styles = TASK_DASHBOARD_UI.caseBadgeStyles || {};
        return (
            <Box mt={2} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8 }}>
                {CASE_TYPE_KEYS.map(({ param, label }) => {
                    const chipStyle = onPrimaryBlue
                        ? {
                              backgroundColor: 'rgba(255,255,255,0.22)',
                              color: '#fff',
                              border: '1px solid rgba(255,255,255,0.45)',
                          }
                        : styles[param] || { backgroundColor: '#eee', color: '#333' };
                    const countDisplay = CommonHelper.numOrDash(this.caseCountForParam(row, param), '0');
                    const countChars = String(countDisplay);
                    const innerPill = this.caseBadgeInnerPillStyle(chipStyle, onPrimaryBlue);
                    return (
                        <span
                            key={param}
                            className={`td-pill-summary${onPrimaryBlue ? ' td-pill-summary--primary' : ''}`}
                            style={{
                                padding: '5px 10px',
                                borderRadius: 16,
                                fontSize: 12,
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                ...chipStyle,
                            }}
                        >
                            <span>{label}</span>
                            <span
                                className="td-pill-inner"
                                style={{
                                    minWidth: 22,
                                    height: 22,
                                    padding: countChars.length > 2 ? '0 5px' : 0,
                                    borderRadius: '999px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    boxSizing: 'border-box',
                                    flexShrink: 0,
                                    ...innerPill,
                                }}
                            >
                                {countDisplay}
                            </span>
                        </span>
                    );
                })}
            </Box>
        );
    }

    renderTabSummaryTotalCircle(summaryBlock, onPrimaryBlue) {
        if (!summaryBlock) return null;
        const totals = this.pickSummaryTotals(summaryBlock);
        const rawTotal = totals && totals.total !== undefined ? totals.total : summaryBlock.total;
        const display =
            rawTotal !== null && rawTotal !== undefined && rawTotal !== ''
                ? String(rawTotal)
                : CommonHelper.numOrDash(rawTotal, '0');
        const circlePx = 40;
        const bg = onPrimaryBlue ? 'rgba(255,255,255,0.22)' : TASK_DASHBOARD_UI.personCountCircleBackground;
        const fg = onPrimaryBlue ? '#fff' : TASK_DASHBOARD_UI.personCountCircleColor;
        const border = onPrimaryBlue ? '1px solid rgba(255,255,255,0.45)' : '1px solid rgba(0,0,0,0.08)';

        return (
            <Box flexShrink={0} display="flex" alignItems="center" style={{ marginLeft: 12 }}>
                <Box
                    style={{
                        width: circlePx,
                        height: circlePx,
                        borderRadius: '50%',
                        backgroundColor: bg,
                        color: fg,
                        border,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 14,
                        boxSizing: 'border-box',
                    }}
                >
                    {display}
                </Box>
            </Box>
        );
    }

    renderTopTabSwitcher() {
        const { mainTab, reportSummary, approvalSummary } = this.state;

        const reportBadges = reportSummary ? (
            this.renderTabSummaryBadges(reportSummary, mainTab === 0)
        ) : (
            <Box aria-hidden style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                {this.renderTabSummaryBadges(TAB_SUMMARY_BADGE_PLACEHOLDER, mainTab === 0)}
            </Box>
        );

        const reportCircle = reportSummary ? (
            this.renderTabSummaryTotalCircle(reportSummary, mainTab === 0)
        ) : (
            <Box aria-hidden style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                {this.renderTabSummaryTotalCircle(TAB_SUMMARY_BADGE_PLACEHOLDER, mainTab === 0)}
            </Box>
        );

        const approvalBadges = (
            <Box aria-hidden style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                {this.renderTabSummaryBadges(TAB_SUMMARY_BADGE_PLACEHOLDER, mainTab === 1)}
            </Box>
        );

        const approvalCircle = approvalSummary ? (
            this.renderTabSummaryTotalCircle(approvalSummary, mainTab === 1)
        ) : (
            <Box aria-hidden style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                {this.renderTabSummaryTotalCircle(TAB_SUMMARY_BADGE_PLACEHOLDER, mainTab === 1)}
            </Box>
        );

        return (
            <Grid
                container
                spacing={2}
                alignItems="stretch"
                component="div"
                role="tablist"
                aria-label="Task dashboard views"
                style={{ marginBottom: 8 }}
            >
                {this.renderMainViewTabCard({
                    tabIndex: 0,
                    title: 'Report On Progress',
                    badgesContent: reportBadges,
                    totalCircleContent: reportCircle,
                })}
                {this.renderMainViewTabCard({
                    tabIndex: 1,
                    title: 'Approval On Progress',
                    badgesContent: approvalBadges,
                    totalCircleContent: approvalCircle,
                })}
            </Grid>
        );
    }

    // =============================================================================
    // View: tables (spreadsheet-style layout per requirement)
    // =============================================================================

    formatTaskDashboardCell(value) {
        if (value === null || value === undefined || value === '') return '';
        const n = Number(value);
        if (!Number.isFinite(n)) return '—';
        return n.toLocaleString('en-US');
    }

    isHighCountPerson(person) {
        const total = person?.total;
        if (total === null || total === undefined) return false;
        const n = Number(total);
        return Number.isFinite(n) && n > TASK_DASHBOARD_UI.highCountThreshold;
    }

    renderHighCountAlertIcon() {
        return (
            <ErrorOutlineIcon
                aria-label="High task count"
                style={{
                    color: TASK_DASHBOARD_UI.highCountAlertIconColor,
                    fontSize: 18,
                    marginLeft: 6,
                    flexShrink: 0,
                }}
            />
        );
    }

    renderJumlahCell(person) {
        const isHighCount = this.isHighCountPerson(person);
        const totalContent =
            person.total !== null && person.total !== undefined
                ? this.formatTaskDashboardCell(person.total)
                : '—';

        return (
            <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                <Box display="inline-flex" alignItems="center" justifyContent="flex-end">
                    {this.renderProjectsLink(person.projectsUrl, 'td-table-num-link', totalContent)}
                    {isHighCount ? this.renderHighCountAlertIcon() : null}
                </Box>
            </TableCell>
        );
    }

    renderProjectsLink(href, className, children) {
        const safe = href && href !== '#' ? href : null;
        if (!safe) return <span className={className}>{children}</span>;
        return (
            <a href={safe} className={className} target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        );
    }

    renderReportCaseCells(person) {
        const { filterBase } = person;
        return CASE_TYPE_KEYS.map(({ param }) => {
            const raw = this.caseCountForParam(person, param);
            const text = this.formatTaskDashboardCell(raw);
            const hasBase = filterBase && typeof filterBase === 'object';
            const href = hasBase ? this.projectsUrlWithCaseType(filterBase, param) : null;
            return (
                <TableCell align="right" key={param} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {this.renderProjectsLink(href, 'td-table-num-link', text)}
                </TableCell>
            );
        });
    }

    renderApprovalRowsSimple(people, emptyKey) {
        const list = Array.isArray(people) ? people : [];
        const filtered = this.filterPeopleForDisplay(list);
        if (filtered.length === 0) {
            return (
                <TableRow key={`empty-${emptyKey}`}>
                    <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="textSecondary">
                            No entries.
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        }

        return filtered.map((person, index) => {
            const isHighCount = this.isHighCountPerson(person);
            return (
            <TableRow
                hover
                key={`${person.id || person.name}-${emptyKey}`}
                className={isHighCount ? 'td-table-row--high-count' : undefined}
            >
                <TableCell style={{ width: 30, paddingLeft: 10, paddingRight: 10 }}>{index + 1}</TableCell>
                <TableCell>
                    {this.renderProjectsLink(person.projectsUrl, 'td-table-name-link', person.name)}
                </TableCell>
                {this.renderJumlahCell(person)}
            </TableRow>
            );
        });
    }

    renderCategoryTable(people, emptyKey, ariaLabel) {
        return (
            <div className="table-wrapper pt-3">
                <Table className="table-list" size="small" aria-label={ariaLabel}>
                    <TableHead>
                        <TableRow>
                            <TableCell className="text-center" style={{ width: 30, paddingLeft: 10, paddingRight: 10 }}>
                                No
                            </TableCell>
                            <TableCell className="text-center">Name</TableCell>
                            <TableCell className="text-center">Jumlah</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{this.renderApprovalRowsSimple(people, emptyKey)}</TableBody>
                </Table>
            </div>
        );
    }

    renderReportRowsDetailed(people, emptyKey) {
        const list = Array.isArray(people) ? people : [];
        const filtered = this.filterPeopleForDisplay(list);
        if (filtered.length === 0) {
            return (
                <TableRow key={`empty-report-${emptyKey}`}>
                    <TableCell colSpan={3 + CASE_TYPE_KEYS.length} align="center">
                        <Typography variant="body2" color="textSecondary">
                            No entries.
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        }
        return filtered.map((person, index) => {
            const isHighCount = this.isHighCountPerson(person);
            return (
            <TableRow
                hover
                key={`${person.id || person.name}-report-${emptyKey}`}
                className={isHighCount ? 'td-table-row--high-count' : undefined}
            >
                <TableCell style={{ width: 30, paddingLeft: 10, paddingRight: 10 }}>{index + 1}</TableCell>
                <TableCell>
                    {this.renderProjectsLink(person.projectsUrl, 'td-table-name-link', person.name)}
                </TableCell>
                {this.renderReportCaseCells(person)}
                {this.renderJumlahCell(person)}
            </TableRow>
            );
        });
    }

    renderReportCategoryTable(people, emptyKey, ariaLabel) {
        return (
            <div className="table-wrapper pt-3">
                <Table className="table-list" size="small" aria-label={ariaLabel}>
                    <TableHead>
                        <TableRow>
                            <TableCell className="text-center" style={{ width: 30, paddingLeft: 10, paddingRight: 10 }}>
                                No
                            </TableCell>
                            <TableCell className="text-center">Name</TableCell>
                            <TableCell className="text-center">HM</TableCell>
                            <TableCell className="text-center">P&I</TableCell>
                            <TableCell className="text-center">Shipowner</TableCell>
                            <TableCell className="text-center">Cargo</TableCell>
                            <TableCell className="text-center">Jumlah</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{this.renderReportRowsDetailed(people, emptyKey)}</TableBody>
                </Table>
            </div>
        );
    }

    renderNotAssignedTable(sectionKey, assignedPeople, detailed) {
        const people = this.peopleWithEmptyTasks(assignedPeople);
        const colSpan = detailed ? 3 + CASE_TYPE_KEYS.length : 3;
        return (
            <div className="table-wrapper pt-3">
                <Table className="table-list" size="small" aria-label={`${sectionKey} not assigned`}>
                    <TableHead>
                        <TableRow>
                            <TableCell className="text-center" style={{ width: 30, paddingLeft: 10, paddingRight: 10 }}>
                                No
                            </TableCell>
                            <TableCell className="text-center">Name</TableCell>
                            {detailed
                                ? CASE_TYPE_KEYS.map(({ param, label }) => (
                                      <TableCell className="text-center" key={param}>
                                          {label}
                                      </TableCell>
                                  ))
                                : null}
                            <TableCell className="text-center">Jumlah</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {people.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={colSpan} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                        No entries.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            people.map((person, index) => (
                                <TableRow hover key={`${person.id || person.name}-${sectionKey}`}>
                                    <TableCell style={{ width: 30, paddingLeft: 10, paddingRight: 10 }}>{index + 1}</TableCell>
                                    <TableCell>
                                        {this.renderProjectsLink(person.projectsUrl, 'td-table-name-link', person.name)}
                                    </TableCell>
                                    {detailed
                                        ? CASE_TYPE_KEYS.map(({ param }) => (
                                              <TableCell align="right" key={param} />
                                          ))
                                        : null}
                                    <TableCell align="right" />
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        );
    }

    renderAssignmentSection({ sectionKey, title, assignedPeople, detailed, assignedContent }) {
        const tab = this.state.sectionTabs[sectionKey] || 0;
        return (
            <Box>
                <h2 className="section-title mb-3">{title}</h2>
                <Tabs
                    className="td-assignment-tabs"
                    value={tab}
                    onChange={(event, value) => this.setSectionTab(sectionKey, value)}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="Assigned" id={`${sectionKey}-tab-0`} aria-controls={`${sectionKey}-panel-0`} />
                    <Tab label="Not Assigned" id={`${sectionKey}-tab-1`} aria-controls={`${sectionKey}-panel-1`} />
                </Tabs>
                <div role="tabpanel" id={`${sectionKey}-panel-${tab}`}>
                    {tab === 0
                        ? assignedContent
                        : this.renderNotAssignedTable(sectionKey, assignedPeople, detailed)}
                </div>
            </Box>
        );
    }

    renderReportProgressTable() {
        const { reportOnProgress } = this.state;
        const sectionTitleStyle = {
            width: '100%',
            backgroundColor: '#f1f3f5',
            border: '1px solid #d0d5dd',
            color: '#344054',
            borderRadius: 4,
            padding: '20px 20px',
            marginBottom: 12,
        };
        return (
            <Box mb={2}>
                <h1 className="page-title mb-5" style={sectionTitleStyle}>
                    Report On Progress
                </h1>
                {this.renderAssignmentSection({
                    sectionKey: 'report-final',
                    title: 'Final Reviewer',
                    assignedPeople: reportOnProgress.topManagement,
                    detailed: true,
                    assignedContent: this.renderReportCategoryTable(
                        reportOnProgress.topManagement,
                        'report-final',
                        'Report final reviewer'
                    ),
                })}
                <div style={{ marginTop: 24 }} />
                {this.renderAssignmentSection({
                    sectionKey: 'report-claim',
                    title: 'Claim Handler',
                    assignedPeople: reportOnProgress.claimHandlers,
                    detailed: true,
                    assignedContent: this.renderReportCategoryTable(
                        reportOnProgress.claimHandlers,
                        'report-claim',
                        'Report claim handler'
                    ),
                })}
                <div style={{ marginTop: 24 }} />
                {this.renderAssignmentSection({
                    sectionKey: 'report-surveyor',
                    title: 'Surveyor',
                    assignedPeople: reportOnProgress.surveyors,
                    detailed: true,
                    assignedContent: this.renderReportCategoryTable(
                        reportOnProgress.surveyors,
                        'report-surveyor',
                        'Report surveyor'
                    ),
                })}
            </Box>
        );
    }

    renderApprovalProgressTable() {
        const { approvalOnProgress } = this.state;
        const sectionTitleStyle = {
            width: '100%',
            backgroundColor: '#f1f3f5',
            border: '1px solid #d0d5dd',
            color: '#344054',
            borderRadius: 4,
            padding: '20px 20px',
            marginBottom: 12,
        };
        return (
            <Box mb={2}>
                <h1 className="page-title mb-5" style={sectionTitleStyle}>
                    Approval On Progress
                </h1>
                {this.renderAssignmentSection({
                    sectionKey: 'approval-final',
                    title: 'Final Reviewer',
                    assignedPeople: approvalOnProgress.topManagement,
                    detailed: false,
                    assignedContent: this.renderCategoryTable(
                        approvalOnProgress.topManagement,
                        'approval-final',
                        'Approval final reviewer'
                    ),
                })}
                <div style={{ marginTop: 24 }} />
                {this.renderAssignmentSection({
                    sectionKey: 'approval-handler',
                    title: 'Approval Handler',
                    assignedPeople: approvalOnProgress.approvalHandlers,
                    detailed: false,
                    assignedContent: this.renderCategoryTable(
                        approvalOnProgress.approvalHandlers,
                        'approval-handler',
                        'Approval handler'
                    ),
                })}
                <div style={{ marginTop: 24 }} />
                {this.renderAssignmentSection({
                    sectionKey: 'approval-surveyor',
                    title: 'Surveyor',
                    assignedPeople: approvalOnProgress.surveyors,
                    detailed: false,
                    assignedContent: this.renderCategoryTable(
                        approvalOnProgress.surveyors,
                        'approval-surveyor',
                        'Approval surveyor'
                    ),
                })}
            </Box>
        );
    }

    render() {
        const { loading, loadError, mainTab } = this.state;

        return (
            <div className="row main-content">
                <div className="col-12 px-lg-5">
                    <h1 className="page-title">Task Dashboard</h1>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                Task Dashboard
                            </li>
                        </ol>
                    </nav>
                </div>

                <div className="col-12 mt-3 px-lg-5 task-dashboard-scope">
                    <style type="text/css">{TASK_DASHBOARD_TABLE_STYLES}</style>
                    {loadError ? (
                        <Paper style={{ padding: 12, marginBottom: 16, backgroundColor: '#fff8e1' }}>
                            <Typography variant="body2">
                                Task dashboard API is unavailable or returned no people. Showing Top Management links from app settings; other sections need API data.
                            </Typography>
                        </Paper>
                    ) : null}

                    {loading ? (
                        <Box display="flex" justifyContent="center" py={6}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {this.renderTopTabSwitcher()}
                            <TabPanel value={mainTab} index={0}>
                                {this.renderReportProgressTable()}
                            </TabPanel>
                            <TabPanel value={mainTab} index={1}>
                                {this.renderApprovalProgressTable()}
                            </TabPanel>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default TaskDashboard;
